import {
  API_PAYMENT_ACTIVE_PROVIDER,
  API_PAYMENT_CONFIRM,
  API_PAYMENT_CREATE,
  API_PAYMENT_DATA,
  API_PAYMENT_FIND_DATA,
  API_PAYMENT_USIO_CHARGE,
  API_PAYMENT_USIO_CONV_FEE,
  API_STRIPE_CLIENT_SESSION_KEY,
  API_STRIPE_INTENT,
  API_STRIPE_INTENT_BY_ID,
} from 'config'
import Api from 'services/api'

export enum PaymentProvider {
  stripe = 'stripe',
  usio = 'usio',
}

export const PaymentProviderTitles: Record<PaymentProvider, string> = {
  [PaymentProvider.stripe]: 'Stripe',
  [PaymentProvider.usio]: 'USIO',
}

/** Merchant portal for USIO (CSR View). No public per-transaction deep link like Stripe. */
export const USIO_DASHBOARD_URL = 'https://csrview.securepds.com/'

export const isUsioPaymentIntent = (intent: { intentId?: string; provider?: PaymentProvider | string }) =>
  intent.provider === PaymentProvider.usio || String(intent.intentId || '').startsWith('usio_')

/** External dashboard URL for a payment intent (Stripe payment page or USIO CSR View). */
export const getPaymentIntentDashboardUrl = (intent: {
  intentId: string
  provider?: PaymentProvider | string
}) => {
  if (isUsioPaymentIntent(intent)) return USIO_DASHBOARD_URL
  return `https://dashboard.stripe.com/payments/${intent.intentId}`
}

export enum StripeMethod {
  us_bank_account = 'us_bank_account',
  card = 'card',
  pinless_debit = 'pinless_debit',
}

export enum PaymentOrderType {
  FeasibilitySign = 'feasibilitySign',
  LoanExtension = 'loanExtension',
  LoanSubmissionRush = 'loanSubmissionRush',
  PullCreditReport = 'pullCreditReport',
  CounterPartyApproval = 'counterPartyApproval',
  RentalLedger = 'rentalLedger',
  ServicingPayment = 'servicingPayment',
}

export const PaymentOrderTypeTitles: Record<PaymentOrderType, string> = {
  [PaymentOrderType.FeasibilitySign]: 'Feasibility Sign',
  [PaymentOrderType.LoanExtension]: 'Loan Extension',
  [PaymentOrderType.LoanSubmissionRush]: 'Invoice',
  [PaymentOrderType.PullCreditReport]: 'Pull Credit Report',
  [PaymentOrderType.CounterPartyApproval]: 'Counterparty Approval',
  [PaymentOrderType.RentalLedger]: 'Rental Ledger',
  [PaymentOrderType.ServicingPayment]: 'Servicing Payment',
}

export const PaymentRefIdTitles: Record<PaymentOrderType, string> = {
  [PaymentOrderType.FeasibilitySign]: 'Appraisal Order Id',
  [PaymentOrderType.LoanExtension]: 'Loan Extension Id',
  [PaymentOrderType.LoanSubmissionRush]: '',
  [PaymentOrderType.PullCreditReport]: 'User',
  [PaymentOrderType.CounterPartyApproval]: 'User',
  [PaymentOrderType.RentalLedger]: 'Rental Ledger Id',
  [PaymentOrderType.ServicingPayment]: 'Loan Number',
}

export const getAvailablePaymentMethods = (
  orderType: PaymentOrderType,
  creditCardPaymentMethods: Record<PaymentOrderType, boolean>,
  achBankTransferPaymentMethods: Record<PaymentOrderType, boolean>,
  pinlessDebitPaymentMethods?: Record<PaymentOrderType, boolean>,
) => {
  // Pinless is listed whenever it is configured; StripeWithMethod drops it unless this area's card
  // payments run through USIO, since only it knows the provider per method.
  return [
    ...(creditCardPaymentMethods?.[orderType] !== false ? [StripeMethod.card] : []),
    ...(achBankTransferPaymentMethods?.[orderType] !== false ? [StripeMethod.us_bank_account] : []),
    ...(pinlessDebitPaymentMethods?.[orderType] ? [StripeMethod.pinless_debit] : []),
  ]
}

export interface IMakePaymentData {
  orderType: PaymentOrderType
  refId: string
  email: string
  amount: number
  [key: string]: any
}

export interface IStripeIntent {
  id: number
  intentId: string
  provider?: PaymentProvider
  email: string
  data: Record<string, any>
  orderType: PaymentOrderType
  refId: string
  method: StripeMethod
  amount: number
  status: StripeIntentStatus
  /** Words shown instead of the status name. Empty means the status name is shown. */
  statusLabel?: string | null
  createdAt: Date
  history: Record<string, any>
  loan: {
    loanNumber: number
    byteproFileName: number
    [key: string]: any
  } | null
}

export enum StripeIntentStatus {
  requires_action = 'requires_action',
  payment_failed = 'payment_failed',
  partially_funded = 'partially_funded',
  processing = 'processing',
  canceled = 'canceled',
  created = 'created',
  succeeded = 'succeeded',
  requires_payment_method = 'requires_payment_method',
  partially_refunded = 'partially_refunded',
  refunded = 'refunded',
}

export const StripeIntentPaidStatus = [
  StripeIntentStatus.created,
  StripeIntentStatus.partially_funded,
  StripeIntentStatus.processing,
  StripeIntentStatus.succeeded,
]

/** Statuses that block creating another payment for the same loan extension */
export const StripeIntentBlockingStatus = [
  ...StripeIntentPaidStatus,
  StripeIntentStatus.payment_failed,
  StripeIntentStatus.canceled,
  StripeIntentStatus.requires_action,
]

/**
 * @param method: us_bank_account | card | pinless_debit
 */
export const getClientSessionKey = (data: {
  orderType: any
  refId: string
  method: string
  amount: number
  data: Record<string, any>
}) => {
  return Api.post(API_PAYMENT_CREATE, data)
}

export const createPayment = getClientSessionKey

export const confirmPayment = (data: Record<string, any>) => {
  return Api.post(API_PAYMENT_CONFIRM, data)
}

/** USIO direct charge with inline field details. Sent once, never cached; caller must not log it. */
export const chargeUsio = (data: Record<string, any>) => {
  return Api.post(API_PAYMENT_USIO_CHARGE, data)
}

/**
 * USIO's convenience fee for an amount + method.
 *
 * USIO prices a card fee from the card's BIN (debit and credit are charged differently), so the full
 * `accountNumber` is required for anything that is not ACH. Without it USIO answers with no fee.
 *
 * `method` comes back RESOLVED and is not always the one sent: a debit card at a tenant with pinless
 * debit enabled for `orderType` is priced under USIO's pinless product, which carries its own fee
 * schedule. Charge under the method returned, not the one asked for, or the declared fee belongs to
 * a program the charge is not running on.
 */
export const getUsioConvenienceFee = (data: {
  amount: number
  method: string
  accountNumber?: string
  orderType?: PaymentOrderType
}): Promise<{
  fee: number
  isConvFee: boolean
  ok: boolean
  message: string
  accountType: string
  method: StripeMethod
  pinless: boolean
  /** The tenant's own fee on top of `fee` (Rules Configuration > Payment Methods). Not declared to USIO. */
  additionalFee?: number
}> => {
  return Api.post(API_PAYMENT_USIO_CONV_FEE, data)
}

export interface IActivePaymentProvider {
  provider: PaymentProvider
  enabled: boolean
  /** The provider behind each method in the area, when an order type was asked about */
  methods?: Partial<Record<StripeMethod, { provider: PaymentProvider; enabled: boolean }>>
}

/**
 * The provider a checkout should use. Credit card and ACH can each run through a different
 * provider per area (Admin > Rules Configuration > Payment Methods), so pass the order type, and
 * the method when it is already known. Without them this is the tenant default.
 */
export const getActivePaymentProvider = (
  orderType?: PaymentOrderType,
  method?: StripeMethod,
): Promise<IActivePaymentProvider> => {
  return Api.get(API_PAYMENT_ACTIVE_PROVIDER, { ...(orderType ? { orderType } : {}), ...(method ? { method } : {}) })
}

export const getStripeIntent = (id: number) => {
  return Api.get(API_STRIPE_INTENT_BY_ID, {}, { id })
}

export const getPaymentData = (data: Record<string, any>) => {
  return Api.post(API_PAYMENT_DATA, data)
}

export const updatePaymentStatus = (id: string) => {
  return Api.post(API_STRIPE_INTENT_BY_ID, {}, { id })
}

export const getStripeIntents = (filters: Record<string, any>) => {
  return Api.get(API_STRIPE_INTENT, filters)
}

export const findStripeIntents = (data: Record<string, any>): Promise<IStripeIntent[]> => {
  return Api.post(API_PAYMENT_FIND_DATA, data)
}

/** @deprecated legacy path — createPayment preferred */
export const getStripeClientSessionKeyLegacy = (data: Record<string, any>) => {
  return Api.post(API_STRIPE_CLIENT_SESSION_KEY, data)
}
